const defaultOptions = {
  enabled: true,
  onError: () => {
  },
  onExecute: () => {
  },
  wait: 0
};
class AsyncThrottler {
  constructor(fn, initialOptions) {
    this.fn = fn;
    this._abortController = null;
    this._executionCount = 0;
    this._isExecuting = false;
    this._isPending = false;
    this._lastExecutionTime = 0;
    this._nextExecutionTime = 0;
    this._options = {
      ...defaultOptions,
      ...initialOptions
    };
  }
  /**
   * Updates the throttler options
   * Returns the new options state
   */
  setOptions(newOptions) {
    this._options = {
      ...this._options,
      ...newOptions
    };
    return this._options;
  }
  /**
   * Returns the current options
   */
  getOptions() {
    return this._options;
  }
  /**
   * Attempts to execute the throttled function
   * If a call is already in progress, it may be blocked or queued depending on the `wait` option
   */
  async maybeExecute(...args) {
    this._lastArgs = args;
    if (this._isPending) return;
    this._isPending = true;
    this._abortController = new AbortController();
    const signal = this._abortController.signal;
    try {
      while (this._isExecuting) {
        await this.delay(this._options.wait, signal);
      }
      while (Date.now() < this._nextExecutionTime) {
        await this.delay(this._nextExecutionTime - Date.now(), signal);
      }
      this._isPending = false;
      this._isExecuting = true;
      await this.executeFunction(...this._lastArgs);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      try {
        this._options.onError(error);
      } catch {
      }
    } finally {
      this._lastExecutionTime = Date.now();
      this._nextExecutionTime = this._lastExecutionTime + this._options.wait;
      this._isExecuting = false;
      this._abortController = null;
    }
  }
  delay(ms, signal) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(resolve, ms);
      signal.addEventListener(
        "abort",
        () => {
          clearTimeout(timeout);
          reject(new Error("AbortError"));
        },
        { once: true }
      );
    });
  }
  async executeFunction(...args) {
    if (!this._options.enabled) return;
    this._executionCount++;
    await this.fn(...args);
    this._options.onExecute(this);
  }
  /**
   * Cancels any pending execution
   */
  cancel() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._isPending = false;
    this._lastArgs = void 0;
  }
  /**
   * Returns the number of times the function has been executed
   */
  getExecutionCount() {
    return this._executionCount;
  }
  /**
   * Returns the last execution time
   */
  getLastExecutionTime() {
    return this._lastExecutionTime;
  }
  /**
   * Returns the next execution time
   */
  getNextExecutionTime() {
    return this._nextExecutionTime;
  }
  /**
   * Returns the current pending state
   */
  getIsPending() {
    return this._isPending;
  }
}
function asyncThrottle(fn, initialOptions) {
  const asyncThrottler = new AsyncThrottler(fn, initialOptions);
  return asyncThrottler.maybeExecute.bind(asyncThrottler);
}
export {
  AsyncThrottler,
  asyncThrottle
};
//# sourceMappingURL=async-throttler.js.map
